import React from "react";

interface LiveEvent {
  event: string;
  scenario?: string;
  scenario_name?: string;
  device_serial?: string;
  step?: string;
  status?: string;
  // ...payload varies per event
  [k: string]: unknown;
}

/**
 * Tail of recent SSE events for this run. Useful to confirm the live wiring
 * is working when the dashboard otherwise looks stuck. Compact one-line
 * format; auto-truncated to the most recent 30.
 */
export default function SSEEventTail({ events }: { events: LiveEvent[] }) {
  if (events.length === 0) return null;
  const recent = events.slice(-30).reverse();

  const colorFor = (event: string) => {
    if (event === "run:execution") return "var(--color-info)";
    if (event === "run:artifact_added") return "var(--color-success)";
    if (event === "run:completed") return "var(--color-text-secondary)";
    if (event.includes("Failed") || event.includes("failed")) return "var(--color-danger)";
    return "var(--color-text-secondary)";
  };

  const summary = (e: LiveEvent): string => {
    const dev = e.device_serial ? ` [${String(e.device_serial).slice(0, 8)}]` : "";
    if (e.event === "run:testCaseStarted") return `▶ ${e.scenario}${dev}`;
    if (e.event === "run:testStepFinished") return `· ${e.status} ${e.step}${dev}`;
    if (e.event === "run:execution") {
      return `${e.status === "passed" ? "✓" : "✗"} ${e.scenario_name}${dev}`;
    }
    if (e.event === "run:artifact_added") {
      const types = Array.isArray(e.types) ? (e.types as string[]).join(",") : "";
      return `📎 ${e.scenarioName}${dev} (${types})`;
    }
    if (e.event === "run:started") return "🚀 run started";
    if (e.event === "run:completed") return `🏁 ${e.status}`;
    return e.event;
  };

  return (
    <div
      style={{
        background: "var(--color-surface)",
        borderRadius: "var(--radius-md)",
        boxShadow: "var(--shadow-card)",
        padding: "var(--space-md)",
        marginBottom: "var(--space-md)",
      }}
    >
      <div
        style={{
          fontSize: "var(--font-size-xs)",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          color: "var(--color-text-secondary)",
          marginBottom: 6,
        }}
      >
        Live events — last {recent.length}
      </div>
      <div
        style={{
          maxHeight: 160,
          overflowY: "auto",
          fontSize: "var(--font-size-xs)",
          fontFamily: "var(--font-mono, monospace)",
          lineHeight: 1.4,
        }}
      >
        {recent.map((e, i) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "120px 1fr",
              gap: 8,
              padding: "1px 0",
            }}
          >
            <span style={{ color: colorFor(e.event), fontWeight: 600 }}>
              {e.event.replace(/^run:/, "")}
            </span>
            <span
              style={{
                color: "var(--color-text-secondary)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {summary(e)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
